import { Box, Stack, Typography } from '@mui/material';
import React, { type ReactNode } from 'react';
import { mainContentScrollSx } from './drawerConstants';

export const PAGE_SHELL_TITLE_SX = {
  fontSize: '1.125rem',
  fontWeight: 600,
} as const;

/** Compact tabs for the page chrome row. */
export const PAGE_SHELL_TABS_SX = {
  minHeight: 36,
  '& .MuiTab-root': { minHeight: 36, py: 0, px: 1.25, fontSize: '0.8125rem' },
} as const;

/** Compact scrollable tabs (long labels stay on one line). */
export const PAGE_SHELL_SCROLLABLE_TABS_SX = {
  minHeight: 36,
  '& .MuiTabs-flexContainer': { alignItems: 'stretch' },
  '& .MuiTab-root': {
    whiteSpace: 'nowrap',
    maxWidth: 'none',
    minHeight: 36,
    minWidth: 'auto',
    py: 0,
    px: 1.25,
    fontSize: '0.8125rem',
  },
} as const;

type PageShellProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  extras?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  testId?: string;
  /**
   * When true, children fill the remaining viewport (internal grid/calendar scroll).
   * Default: the page body scrolls so autoHeight tables and long content stay reachable.
   */
  fill?: boolean;
};

/**
 * Shared page chrome: title / subtitle / tabs / actions on one horizontal row,
 * with the remaining viewport reserved for scrollable center content.
 */
export function PageShell({
  title,
  subtitle,
  extras,
  actions,
  children,
  testId,
  fill = false,
}: PageShellProps) {
  return (
    <Box
      data-testid={testId}
      sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ flexShrink: 0, pb: 0.75, rowGap: 0.5 }}
      >
        <Typography variant="h1" component="h1" sx={PAGE_SHELL_TITLE_SX}>
          {title}
        </Typography>
        {subtitle == null ? null : typeof subtitle === 'string' || typeof subtitle === 'number' ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : (
          subtitle
        )}
        {extras}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        {actions}
      </Stack>
      <Box
        sx={
          fill
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                pt: 0.75,
                borderTop: 1,
                borderColor: 'divider',
              }
            : {
                ...mainContentScrollSx,
                display: 'block',
                pt: 0.75,
                borderTop: 1,
                borderColor: 'divider',
              }
        }
      >
        {children}
      </Box>
    </Box>
  );
}
